import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Button,
  Card,
  Grid,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import { CarAvailability } from '../../enum';
import { getAssetURL } from '../../utils/url';
import { IsNotNullOrEmpty } from '../../utils/validation';
import Loader from '../Loader';
import { BUTTON_BG_COLOR } from './PropertySpotlightDialog';
import { WidgetConfig } from '../../types';

interface ViewSimilarPropertiesDialogProps {
  toggle: boolean;
  onClose: () => void;
  gotoLoginPage: () => void;
  onVehicleSelected: (vehicle: any) => void;
  onGetTodaysPriceClicked: (vehicle: any) => void;
  carData: any;
  api: any;
  config: WidgetConfig;
}

const ViewSimilarPropertiesDialog: React.FC<ViewSimilarPropertiesDialogProps> = ({
  toggle,
  onClose,
  gotoLoginPage,
  onVehicleSelected,
  onGetTodaysPriceClicked,
  carData,
  api,
  config,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [data, setData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const fetchingRef = useRef(false);

  const getNumericValue = (value: unknown): number | undefined => {
    if (value === null || value === undefined || value === '') return undefined;
    const numericValue = Number(String(value).replace(/[^0-9.-]/g, ''));
    return Number.isFinite(numericValue) ? numericValue : undefined;
  };

  const buildSimilarPropertiesQuery = () => {
    const listPrice = getNumericValue(carData?.listPrice ?? carData?.price);
    const minPrice = listPrice ? Math.max(0, Math.round(listPrice * 0.8)) : undefined;
    const maxPrice = listPrice ? Math.round(listPrice * 1.2) : undefined;
    const propertyType = carData?.propertyType || carData?.type;

    return {
      ...(IsNotNullOrEmpty(carData?.city) && {
        city: carData.city,
      }),
      ...(IsNotNullOrEmpty(propertyType) && {
        propertyType,
      }),
      ...(IsNotNullOrEmpty(carData?.id) && {
        excludePropertyId: carData.id,
      }),
      ...(minPrice !== undefined && {
        minPrice,
      }),
      ...(maxPrice !== undefined && {
        maxPrice,
      }),
      limit: 10,
      skip: 0,
    };
  };

  useEffect(() => {
    const fetchSimilarCars = async () => {
      if (fetchingRef.current) return;

      if (IsNotNullOrEmpty(carData)) {
        try {
          fetchingRef.current = true;
          setIsLoading(true);
          const result = await api.getSimilarVehicles(buildSimilarPropertiesQuery());

          setData(result?.data || []);
        } catch (error) {
          console.error(error);
        } finally {
          setIsLoading(false);
          fetchingRef.current = false;
        }
      }
    };

    fetchSimilarCars();
  }, [carData, api]);

  const onViewVehicleClicked = (selectedVehicle: any) => {
    onVehicleSelected(selectedVehicle);
    onCloseDialog();
  };

  const onCloseDialog = () => {
    setData([]);
    onClose();
  };

  return (
    <>
      <Dialog
        open={toggle}
        onClose={onCloseDialog}
        maxWidth='xs'
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-container': {
            alignItems: isMobile ? 'stretch' : 'flex-end',
            justifyContent: isMobile ? 'center' : 'flex-end',
          },
        }}
        PaperProps={{
          sx: {
            bgcolor: '#E8EBF4',
            backgroundImage: 'none',
            maxHeight: isMobile ? '100vh' : '80vh',
            m: isMobile ? 0 : 2,
            mb: isMobile ? 0 : '85px',
          },
        }}
        id='confirmation-dialog'
      >
        <DialogTitle sx={{ p: 'auto', position: 'relative' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              // justifyContent: 'center',
              width: '100%',
              position: 'relative',
            }}
          >
            <IconButton
              onClick={() => onCloseDialog()}
              sx={{
                left: 0,
                py: 0.5,
                // position: 'absolute',
              }}
              aria-label='Go back'
            >
              <KeyboardBackspaceIcon
                fontSize='large'
                sx={{ color: 'primary' }}
              />
            </IconButton>

            <Typography fontWeight={400} color={'primary.light'}>
              Back to Property
            </Typography>

            {/* <Box
              component='img'
              src='https://miles-cdn.qrbuddy.ai/static/qr_buddy_blue_logo.png'
              alt='Logo Icon'
              sx={{
                height: 25,
                width: 'auto',
                objectFit: 'contain',
                // filter: 'brightness(100)',
              }}
            /> */}

            {/* {!loggedInUserId && (
              <Button
                variant='contained'
                sx={{
                  background: '#fff',
                  color: 'text.primary',
                  borderRadius: '12px',
                  border: '1px solid #44b0d5',
                  fontWeight: 400,
                  position: 'absolute',
                  right: 0,
                  py: 0.5,
                  boxShadow: 1,
                }}
                onClick={gotoLoginPage}
              >
                {t('button.log_in')}
              </Button>
            )} */}
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{ mt: 2, mb: 1, px: { xs: 1.5, sm: 4 }, textAlign: 'center' }}
        >
          {isLoading && (
            <Box
              sx={{
                bgcolor: 'white',
                width: '100%',
                display: 'flex',
                minHeight: '75vh',
                alignItems: 'center',
              }}
            >
              <Loader />
            </Box>
          )}
          {!isLoading && data.length === 0 && (
            <Box
              sx={{
                bgcolor: 'white',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mt: 2,
                p: 5,
              }}
            >
              <Typography
                sx={{
                  fontSize: '16px',
                  color: '#000',
                }}
              >
                No property found.
              </Typography>
            </Box>
          )}
          <Box
            display='flex'
            alignItems='center'
            width='100%'
            pr={2}
            sx={{ px: { xs: 2, sm: 0 }, mt: 1 }}
          >
            <Grid container mt={2} spacing={2}>
              {data.map((item: any) => {
                // const { symbolOrCode, amount } = parseCurrencyString(item?.price);
                return (
                  <Grid
                    size={{ xs: 12, sm: 12, md: 12 }}
                    key={item.id}
                    // ref={
                    //   idx + 1 === data?.length ? lastElementRef : undefined
                    // }
                  >
                    <Card
                      sx={{
                        borderRadius: '16px',
                        // padding: '12px',
                        paddingBottom: '12px',
                        backgroundColor: '#FFFFFF',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        boxShadow: '0 0 15px 0px rgba(0, 0, 0, 0.15)',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                          cursor: 'pointer',
                        },
                      }}
                      onClick={() => {
                        onViewVehicleClicked(item);
                      }}
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          width: '100%',
                          height: '200px',
                          // borderRadius: '6px',
                          overflow: 'hidden',
                        }}
                      >
                        {item?.photos?.[0]?.key || item?.photos?.[0]?.url ? (
                          <Box
                            component='img'
                            src={
                              item?.photos[0]?.key
                                ? getAssetURL(
                                    config.assetsUrl,
                                    item?.photos[0]?.key,
                                    item?.photos[0]?.url,
                                  )
                                : item?.photos[0]?.url
                            }
                            alt={item?.title}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              display: 'block',
                              cursor: 'pointer',
                              opacity:
                                item?.availability !== CarAvailability.available
                                  ? 0.5
                                  : 1,
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: '#0C1E3C',
                              color: '#C9A84C',
                            }}
                          >
                            <HomeWorkIcon sx={{ fontSize: 48 }} />
                          </Box>
                        )}

                        {item?.availability !== CarAvailability.available && (
                          <Box
                            component='img'
                            src='https://miles-cdn.qrbuddy.ai/static/sold_out.png'
                            alt='Sold Out'
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              maxWidth: '80%',
                              maxHeight: '80%',
                              pointerEvents: 'none',
                              userSelect: 'none',
                            }}
                          />
                        )}
                      </Box>

                      {/* <Divider sx={{ my: 1, mt: 1 }} /> */}
                      {/* Car Name & Badge */}
                      <Grid
                        container
                        spacing={1}
                        sx={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'space-between',
                          mt: 1,
                        }}
                      >
                        <Grid
                          size={6.5}
                          sx={{ gap: 0.2, ml: 1, textAlign: 'start' }}
                        >
                          <Typography fontSize='14px' fontWeight={500}>
                            {item?.title}
                          </Typography>
                          {/* <Typography
                          sx={{
                            fontSize: '18px',
                            fontWeight: 600,
                            background: (theme: any) =>
                              theme.palette.gradients.primary,
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            color: 'transparent',
                          }}
                        >
                          {parsedPrice?.amount !== '-'
                            ? `${parsedPrice?.symbolOrCode}${parsedPrice?.amount}`
                            : '-'}
                        </Typography> */}
                          <Typography
                            fontSize='12px'
                            fontWeight={400}
                            color={'text.secondary'}
                          >
                            {[
                              item?.bedsTotal || item?.bedrooms
                                ? `${item?.bedsTotal || item?.bedrooms} beds`
                                : null,
                              item?.bathroomsTotalInteger || item?.bathroomsFull
                                ? `${item?.bathroomsTotalInteger || item?.bathroomsFull} baths`
                                : null,
                              item?.livingArea || item?.area
                                ? `${Number(item?.livingArea || item?.area).toLocaleString()} sq ft`
                                : null,
                            ]
                              .filter(Boolean)
                              .join(' - ')}
                          </Typography>
                        </Grid>
                        <Grid
                          size={5}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                          }}
                        >
                          <Button
                            variant='contained'
                            fullWidth
                            sx={{
                              background: BUTTON_BG_COLOR,
                              // fontSize: { xs: '0.768rem', sm: '1rem' },
                              fontSize: '0.768rem',
                              lineHeight: 1,
                              fontWeight: 500,
                              px: 1,
                              textTransform: 'none',
                            }}
                            onClick={() => {
                              onGetTodaysPriceClicked(item);
                              onCloseDialog();
                            }}
                          >
                            Request Payment Estimate
                          </Button>
                          <Button
                            variant='contained'
                            fullWidth
                            sx={{
                              background: BUTTON_BG_COLOR,
                              // fontSize: { xs: '0.768rem', sm: '1rem' },
                              fontSize: '0.768rem',
                              lineHeight: 1,
                              fontWeight: 500,
                              px: 1,
                              textTransform: 'none',
                            }}
                            onClick={() => {
                              onViewVehicleClicked(item);
                            }}
                          >
                            View Property
                          </Button>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ViewSimilarPropertiesDialog;
